
import React from "react";

const Privacypolicy = () => {
  return (
    <div className="bg-light min-vh-100 py-4 py-md-5">
      <div className="container">
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body p-3 p-md-5">

            {/* Header */}
            <div className="text-center border-bottom pb-4 mb-4">
              <h1 className="fw-bold mb-2">
                Privacy Policy
              </h1>

              <p className="text-muted mb-0">
                Last Updated: September 1, 2026
              </p>
            </div>

            {/* Introduction */}
            <p>
              This Privacy Policy describes how Dectwin Billing ("we", "our",
              "us") collects, uses, stores, and protects information when you
              use the Dectwin Billing mobile application and related services.
            </p>

            <p>
              By using Dectwin Billing, you agree to the practices described in
              this Privacy Policy.
            </p>


            {/* 1. Information We Collect */}
            <section className="mb-5">
              <h2 className="h4 fw-bold border-bottom pb-2 mb-3">
                1. Information We Collect
              </h2>

              <p>
                Depending on the features you use, Dectwin Billing may collect
                the following information:
              </p>

              <h3 className="h5 fw-semibold mt-4">
                Account and Business Information
              </h3>

              <p>
                When you create or manage an account, we may collect:
              </p>

              <ul>
                <li>Name</li>
                <li>Mobile phone number</li>
                <li>Email address</li>
                <li>Business/shop name</li>
                <li>Business address</li>
                <li>GST or tax-related information, where provided</li>
                <li>
                  Other information required to create and manage your
                  business account
                </li>
              </ul>


              <h3 className="h5 fw-semibold mt-4">
                Product and Inventory Information
              </h3>

              <p>
                To provide billing and inventory management functionality, the
                application may collect and store:
              </p>

              <ul>
                <li>Product names</li>
                <li>Product categories</li>
                <li>Product quantities and stock information</li>
                <li>Purchase prices</li>
                <li>Selling prices</li>
                <li>MRP</li>
                <li>Tax information</li>
                <li>Discount information</li>
                <li>Manufacturing and expiry dates</li>
                <li>Barcode information</li>
                <li>Supplier/dealer information</li>
                <li>Invoice and billing information</li>
              </ul>


              <h3 className="h5 fw-semibold mt-4">
                Customer Information
              </h3>

              <p>
                If you choose to enter customer information into the
                application, we may process information such as:
              </p>

              <ul>
                <li>Customer name</li>
                <li>Customer phone number</li>
                <li>Customer address</li>
                <li>Purchase/invoice information</li>
                <li>Amounts related to transactions</li>
              </ul>

              <p>
                You are responsible for ensuring that you have the appropriate
                permission or legal basis to enter and use information about
                your customers.
              </p>


              <h3 className="h5 fw-semibold mt-4">
                Images and Documents
              </h3>

              <p>
                If the application provides features for uploading documents
                or images, such as business or identity-related documents,
                those files may be collected and securely processed for the
                specific purpose for which they are submitted.
              </p>


              <h3 className="h5 fw-semibold mt-4">
                Camera and Barcode Information
              </h3>

              <p>
                Dectwin Billing may request access to your device camera when
                you use barcode scanning or other camera-based features.
              </p>

              <p>
                Camera access is used only to provide the relevant
                functionality. We do not use the camera to continuously
                monitor you.
              </p>


              <h3 className="h5 fw-semibold mt-4">
                Device and Technical Information
              </h3>

              <p>
                We may automatically receive limited technical information
                required to operate and secure the application, such as:
              </p>

              <ul>
                <li>Device type</li>
                <li>Operating system version</li>
                <li>Application version</li>
                <li>IP address</li>
                <li>Basic diagnostic information</li>
                <li>Error and crash information</li>
              </ul>

              <p>
                We use this information to maintain, secure, troubleshoot, and
                improve our services.
              </p>
            </section>


            {/* 2 */}
            <section className="mb-5">
              <h2 className="h4 fw-bold border-bottom pb-2 mb-3">
                2. How We Use Your Information
              </h2>

              <p>
                We use collected information for purposes including:
              </p>

              <ul>
                <li>Creating and managing your account</li>
                <li>Providing billing and invoice functionality</li>
                <li>Managing products and inventory</li>
                <li>Processing and displaying business information</li>
                <li>Providing barcode scanning functionality</li>
                <li>Generating invoices and reports</li>
                <li>
                  Providing customer and dealer management functionality
                </li>
                <li>Synchronizing information with our servers</li>
                <li>Maintaining and improving the application</li>
                <li>
                  Detecting and preventing fraud, abuse, and unauthorized
                  access
                </li>
                <li>Troubleshooting technical problems</li>
                <li>
                  Communicating with you regarding your account or our
                  services
                </li>
                <li>Complying with applicable legal obligations</li>
              </ul>

              <p>
                We do not use personal information for purposes unrelated to
                the services described in this Privacy Policy without
                appropriate notice or consent where required.
              </p>
            </section>


            {/* 3 */}
            <section className="mb-5">
              <h2 className="h4 fw-bold border-bottom pb-2 mb-3">
                3. How We Share Information
              </h2>

              <p>
                <strong>We do not sell your personal information.</strong>
              </p>

              <p>
                We may share or disclose information only when reasonably
                necessary to provide and operate our services, including:
              </p>

              <h3 className="h5 fw-semibold mt-4">
                Service Providers
              </h3>

              <p>
                We may use trusted third-party service providers for services
                such as:
              </p>

              <ul>
                <li>Cloud hosting</li>
                <li>Database infrastructure</li>
                <li>Authentication</li>
                <li>Application monitoring</li>
                <li>Crash reporting</li>
                <li>Communication services</li>
                <li>
                  Other infrastructure required to operate the application
                </li>
              </ul>

              <p>
                These providers may process information only as necessary to
                provide their services to us.
              </p>

              <h3 className="h5 fw-semibold mt-4">
                Legal Requirements
              </h3>

              <p>
                We may disclose information when required to do so by
                applicable law, regulation, legal process, court order, or
                governmental request.
              </p>

              <h3 className="h5 fw-semibold mt-4">
                Business Transfers
              </h3>

              <p>
                If our business is involved in a merger, acquisition,
                restructuring, sale of assets, or similar transaction,
                information may be transferred as part of that transaction,
                subject to applicable law.
              </p>
            </section>


            {/* 4 */}
            <section className="mb-5">
              <h2 className="h4 fw-bold border-bottom pb-2 mb-3">
                4. Data Security
              </h2>

              <p>
                We take reasonable technical and organizational measures to
                protect your information from unauthorized access, alteration,
                disclosure, loss, or destruction.
              </p>

              <p>
                Depending on the nature of the information, security measures
                may include:
              </p>

              <ul>
                <li>Encrypted communication using HTTPS/TLS</li>
                <li>Authentication and access controls</li>
                <li>Secure server infrastructure</li>
                <li>Restricted access to personal information</li>
                <li>Security monitoring and logging</li>
                <li>Regular maintenance and security updates</li>
              </ul>

              <p>
                However, no method of electronic transmission or storage is
                completely secure. Therefore, we cannot guarantee absolute
                security.
              </p>
            </section>


            {/* 5 */}
            <section className="mb-5">
              <h2 className="h4 fw-bold border-bottom pb-2 mb-3">
                5. Data Retention
              </h2>

              <p>
                We retain information only for as long as reasonably necessary
                to:
              </p>

              <ul>
                <li>Provide our services</li>
                <li>Maintain your account</li>
                <li>Maintain business and transaction records</li>
                <li>
                  Meet legal, accounting, tax, or regulatory requirements
                </li>
                <li>Resolve disputes</li>
                <li>Enforce agreements</li>
                <li>Prevent fraud and abuse</li>
              </ul>

              <p>
                When information is no longer required, we may securely delete
                or anonymize it, subject to applicable legal and regulatory
                requirements.
              </p>
            </section>


            {/* 6 */}
            <section className="mb-5">
              <h2 className="h4 fw-bold border-bottom pb-2 mb-3">
                6. Account and Data Deletion
              </h2>

              <p>
                If your Dectwin Billing account supports account creation, you
                may request deletion of your account and associated personal
                data.
              </p>

              <p>
                To request deletion, contact us using the contact information
                provided below.
              </p>

              <p>
                When a valid deletion request is received, we will delete or
                anonymize applicable personal information, subject to
                information that we are legally required or permitted to
                retain.
              </p>

              <p>
                Certain business, billing, tax, or transaction records may
                need to be retained for a legally required period.
              </p>

              <p>
                Google Play requires apps offering account creation to provide
                users with a clear method to request account deletion and to
                delete associated data when appropriate.
              </p>
            </section>


            {/* 7 */}
            <section className="mb-5">
              <h2 className="h4 fw-bold border-bottom pb-2 mb-3">
                7. Children's Privacy
              </h2>

              <p>
                Dectwin Billing is intended for business and general users and
                is not specifically directed toward children.
              </p>

              <p>
                We do not knowingly collect personal information from children
                where prohibited by applicable law.
              </p>

              <p>
                If you believe that a child has provided personal information
                to us improperly, please contact us so that we can investigate
                and take appropriate action.
              </p>
            </section>


            {/* 8 */}
            <section className="mb-5">
              <h2 className="h4 fw-bold border-bottom pb-2 mb-3">
                8. Third-Party Services
              </h2>

              <p>
                The application may use third-party services necessary to
                provide functionality, security, analytics, hosting, or other
                infrastructure.
              </p>

              <p>
                Third-party providers may have their own privacy policies
                governing their processing of information.
              </p>

              <p>
                We recommend reviewing the privacy policies of third-party
                services integrated into the application.
              </p>
            </section>


            {/* 9 */}
            <section className="mb-5">
              <h2 className="h4 fw-bold border-bottom pb-2 mb-3">
                9. Your Responsibilities
              </h2>

              <p>
                If you use Dectwin Billing to store information about
                customers, employees, dealers, suppliers, or other
                individuals, you are responsible for:
              </p>

              <ul>
                <li>
                  Providing appropriate notices to those individuals where
                  required
                </li>
                <li>Obtaining required permissions or consent</li>
                <li>Using the information lawfully</li>
                <li>Keeping account credentials secure</li>
                <li>
                  Not entering information that you do not have the right to
                  process
                </li>
              </ul>
            </section>


            {/* 10 */}
            <section className="mb-5">
              <h2 className="h4 fw-bold border-bottom pb-2 mb-3">
                10. Changes to This Privacy Policy
              </h2>

              <p>
                We may update this Privacy Policy from time to time.
              </p>

              <p>
                When we make changes, we will update the Last Updated date at
                the beginning of this policy.
              </p>

              <p>
                We encourage you to periodically review this page to remain
                informed about how we handle information.
              </p>
            </section>


            {/* 11 */}
            <section className="mb-5">
              <h2 className="h4 fw-bold border-bottom pb-2 mb-3">
                11. Contact Us
              </h2>

              <p>
                If you have questions about this Privacy Policy, your personal
                information, or a data deletion request, please contact us.
              </p>

              <div className="bg-light rounded-3 p-3 p-md-4">
                <p className="mb-2">
                  <strong>App:</strong> Dectwin Billing
                </p>

    

       
                <p className="mb-0">
                  <strong>Email:</strong>{" "}
                  dectwin@gmail.com
                </p>
              </div>

            
            </section>


            {/* 12 */}
            <section className="mb-2">
              <h2 className="h4 fw-bold border-bottom pb-2 mb-3">
                12. Consent
              </h2>

              <p className="mb-0">
                By using Dectwin Billing, you acknowledge that you have read
                and understood this Privacy Policy and agree to the collection
                and use of information as described above.
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacypolicy;

